import React, { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import FileUpload from '@/components/FileUpload';
import VoiceRecognition from '@/components/VoiceRecognition';
import FreeTextNotice from '@/components/FreeTextNotice';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Général', 'Sûreté', 'Incendie', 'Secours à personnes', 'Technique', 'Divers'];

const ConsigneForm = ({ sites, consigne, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    category: consigne?.category || '',
    title: consigne?.title || '',
    content: consigne?.content || '',
    site_id: consigne?.site_id || '',
    attachments: consigne?.attachments || [],
    requires_signature: consigne?.requires_signature || false,
    start_date: consigne?.start_date || '',
    end_date: consigne?.end_date || '',
    start_time: consigne?.start_time || '',
    end_time: consigne?.end_time || ''
  });
  const [isTemporary, setIsTemporary] = useState(!!(consigne?.start_date || consigne?.end_date));
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isTemporary && (!formData.start_date || !formData.end_date)) {
      toast.error('Indiquez les deux dates de la période concernée');
      return;
    }
    if (isTemporary && formData.end_date < formData.start_date) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    if (isTemporary && formData.start_date === formData.end_date && formData.start_time && formData.end_time && formData.end_time <= formData.start_time) {
      toast.error('L\'heure de fin doit être après l\'heure de début');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');

    try {
      const url = consigne 
        ? `${BACKEND_URL}/api/consignes/${consigne.id}`
        : `${BACKEND_URL}/api/consignes`;
      
      const method = consigne ? 'PUT' : 'POST';

      const payload = {
        ...formData,
        start_date: isTemporary ? formData.start_date : null,
        end_date: isTemporary ? formData.end_date : null,
        start_time: isTemporary ? (formData.start_time || null) : null,
        end_time: isTemporary ? (formData.end_time || null) : null
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        onSuccess();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="consigne-form">
      <div className="space-y-2">
        <Label htmlFor="site">Site *</Label>
        <Select 
          value={formData.site_id} 
          onValueChange={(value) => setFormData({ ...formData, site_id: value })}
          required
        >
          <SelectTrigger id="site" data-testid="consigne-site">
            <SelectValue placeholder="Sélectionnez un site" />
          </SelectTrigger>
          <SelectContent>
            {sites.map((site) => (
              <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="category">Catégorie *</Label>
        <Select 
          value={formData.category} 
          onValueChange={(value) => setFormData({ ...formData, category: value })}
          required
        >
          <SelectTrigger id="category" data-testid="consigne-category">
            <SelectValue placeholder="Sélectionnez une catégorie" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((cat) => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Titre *</Label>
        <Input
          id="title"
          data-testid="consigne-title"
          placeholder="Titre de la consigne"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="content">Contenu *</Label>
        <VoiceRecognition 
          onTranscript={(chunk) => setFormData(prev => ({ ...prev, content: prev.content + chunk }))}
          fieldName="content"
        />
        <Textarea
          id="content"
          data-testid="consigne-content"
          placeholder="Détaillez la consigne ou utilisez la dictée vocale..."
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          rows={8}
          required
        />
        <FreeTextNotice />
      </div>

      <div className="space-y-2">
        <Label>Pièces jointes</Label>
        <FileUpload
          onFilesUploaded={(fileIds) => setFormData({ ...formData, attachments: fileIds })}
          existingFiles={consigne?.attachments || []}
        />
      </div>

      <div className="flex items-center space-x-3 p-3 bg-orange-50 dark:bg-orange-950/30 rounded-lg border border-orange-200 dark:border-orange-900">
        <Checkbox
          id="requires_signature"
          checked={formData.requires_signature}
          onCheckedChange={(checked) => setFormData({ ...formData, requires_signature: checked })}
          data-testid="requires-signature"
        />
        <div className="flex-1">
          <Label htmlFor="requires_signature" className="text-sm font-medium cursor-pointer text-slate-800 dark:text-slate-100">
            Signature obligatoire
          </Label>
          <p className="text-xs text-gray-500 dark:text-slate-400">Les agents devront signer pour confirmer la lecture</p>
        </div>
      </div>

      <div className="p-3 bg-indigo-50 dark:bg-indigo-950/30 rounded-lg border border-indigo-200 dark:border-indigo-900 space-y-3">
        <div className="flex items-center space-x-3">
          <Checkbox
            id="is_temporary"
            checked={isTemporary}
            onCheckedChange={(checked) => setIsTemporary(!!checked)}
            data-testid="is-temporary"
          />
          <div className="flex-1">
            <Label htmlFor="is_temporary" className="text-sm font-medium cursor-pointer text-slate-800 dark:text-slate-100">
              Consigne temporaire liée à un événement ponctuel
            </Label>
            <p className="text-xs text-gray-500 dark:text-slate-400">
              Affiche un encadré discret "Consigne temporaire en cours" sur la main courante pendant la période. Une fois
              terminée, la consigne n'est plus visible que par les chefs de poste et administrateurs.
            </p>
          </div>
        </div>
        {isTemporary && (
          <div className="grid grid-cols-2 gap-3 pl-9">
            <div>
              <Label htmlFor="start_date" className="text-xs text-slate-700 dark:text-slate-300">Du</Label>
              <div className="flex gap-2">
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  data-testid="consigne-start-date"
                />
                <Input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-28"
                  title="Heure de début (facultatif)"
                  data-testid="consigne-start-time"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="end_date" className="text-xs text-slate-700 dark:text-slate-300">Au</Label>
              <div className="flex gap-2">
              <Input
                id="end_date"
                type="date"
                min={formData.start_date || undefined}
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                data-testid="consigne-end-date"
              />
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-28"
                title="Heure de fin (facultatif)"
                data-testid="consigne-end-time"
              />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" className="btn-primary flex-1" disabled={loading} data-testid="submit-consigne">
          {loading ? 'Enregistrement...' : consigne ? 'Mettre à jour' : 'Créer la consigne'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>Annuler</Button>
      </div>
    </form>
  );
};

export default ConsigneForm;
